import { generatePath, matchPath } from 'react-router';
import { useLocation } from 'react-router-dom';

/**
 * Pages where the current URL, with the new brand ID swapped in, is always valid. Switching brand
 * stays on these pages. Kept short on purpose: these are the most visited such pages in Datadog
 * RUM. To add one, see doc/adr/0009-brand-switch-allow-list.md.
 */
export const BRAND_SWITCH_ALLOW_LIST = [
  '/:brandId/orders',
  '/:brandId/sales',
  '/:brandId/properties',
  '/:brandId/restaurant-menus',
  '/:brandId/setup',
  '/:brandId/sales-channels/website',
  '/:brandId/reports/dashboards',
  '/:brandId/finance/bank-accounts',
  '/:brandId/menu-editor/menus',
  '/:brandId/vouchers',
];

type PageLocation = { pathname: string; search?: string; hash?: string };

/**
 * Where to go after switching to `brandId`: the same page under the new brand when the page is on
 * the allow-list, otherwise the new brand's home. A query string or hash also means home, as it
 * can hold one brand's record IDs, e.g. `?store=555`.
 */
export const getBrandSwitchPath = (brandId: string, { pathname, search, hash }: PageLocation) => {
  const homePath = `/${brandId}/home`;
  if (search || hash) {
    return homePath;
  }
  const allowedPath = BRAND_SWITCH_ALLOW_LIST.find((path) =>
    matchPath(pathname, { path, exact: true })
  );
  return allowedPath ? generatePath(allowedPath, { brandId }) : homePath;
};

/** Returns `getBrandSwitchPath` for the current page. */
export const useBrandSwitchPath = () => {
  const location = useLocation();
  return (brandId: string) => getBrandSwitchPath(brandId, location);
};
